import {
  addMonths,
  endOfToday,
  differenceInCalendarDays,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInYears,
  startOfDay,
  parseISO,
  format,
  isPast,
  isFuture,
  getISOWeek,
  startOfISOWeek,
  endOfISOWeek,
  getISOWeeksInYear,
  differenceInCalendarISOWeeks,
  isMonday,
  isSameMonth,
  subWeeks,
  endOfDay,
  addDays,
  addHours,
  subDays,
  isSameDay,
  isEqual,
  startOfHour,
  getQuarter,
  differenceInMilliseconds,
  startOfYear,
  endOfYear,
  isAfter,
  isSameYear,
  differenceInCalendarYears,
  getYear,
  startOfMonth,
  endOfMonth,
  isWithinInterval,
  addYears,
  subYears,
  getMonth,
  getDate,
} from 'date-fns';

export const formatDate = (date, formatKey = 'dd MMM y') =>
  date ? format(new Date(date), formatKey) : undefined;

export const formatTimeAgo = (date, translations) => {
  if (!date) {
    return undefined;
  }

  const now = new Date();
  const then = new Date(date);
  const minutes = differenceInMinutes(now, then);

  if (minutes < 1) {
    return translations.justNow;
  }

  if (minutes < 60) {
    return `${minutes} ${
      minutes === 1 ? translations.min : translations.mins
    } ${translations.ago}`;
  }

  const hours = differenceInHours(now, then);

  if (hours < 24) {
    return `${hours} ${hours === 1 ? translations.hour : translations.hours} ${
      translations.ago
    }`;
  }

  const years = differenceInYears(now, then);

  if (years >= 1) {
    return `${years} ${years === 1 ? translations.year : translations.years} ${
      translations.ago
    }`;
  }

  const days = differenceInDays(now, then);

  return `${days} ${days === 1 ? translations.day : translations.days} ${
    translations.ago
  }`;
};

export const getDifferenceInDays = date => {
  const today = startOfDay(new Date());

  return differenceInCalendarDays(parseISO(date), today);
};

export const getSurveyDurationDays = (startDate, expireDate) => {
  if (!startDate || !expireDate) {
    return 0;
  }

  const days = differenceInCalendarDays(
    new Date(expireDate),
    new Date(startDate)
  );

  return Number.isFinite(days) ? Math.max(days, 0) : 0;
};

export const areEqualInMilliseconds = (date1, date2) => {
  return differenceInMilliseconds(parseISO(date1), parseISO(date2));
};

export const isDateInPast = date => isPast(new Date(date));

export const isDateSoonToExpire = (date, warningDays = 0) => {
  if (!date) {
    return false;
  }

  if (!warningDays || isDateInPast(date)) {
    return false;
  }

  const daysUntil = getDifferenceInDays(date);

  return (
    Number.isFinite(daysUntil) &&
    daysUntil >= 0 &&
    daysUntil <= Number(warningDays)
  );
};

export const isDateInFuture = date => isFuture(new Date(date));

export const getWeekNumberInYear = date => getISOWeek(new Date(date));

export const getNumberOfWeeksInYear = date => getISOWeeksInYear(new Date(date));

export const getNumberOfWeeksInRange = (dateStart, dateEnd) =>
  differenceInCalendarISOWeeks(new Date(dateEnd), new Date(dateStart));

export const getStartOfWeek = date => startOfISOWeek(new Date(date));

export const getEndOfWeek = date => endOfISOWeek(new Date(date));

export const getStartOfDay = date => startOfDay(new Date(date));

export const getStartOfMonth = date => startOfMonth(new Date(date));

export const getEndOfMonth = date => endOfMonth(new Date(date));

export const isSameMonthCheck = (currentDate, previousDate) =>
  isSameMonth(new Date(previousDate), new Date(currentDate));

export const isFirstWeekOfTheMonth = weekStart => {
  const startOfWeek = new Date(weekStart);
  const previousWeek = subWeeks(startOfWeek, 1);

  return isMonday(startOfWeek) && !isSameMonth(startOfWeek, previousWeek);
};

export const getTimeRangeFromToday = (months = 6) => ({
  start: formatDate(addMonths(new Date(), -months), 'yyyy-LL-dd'),
  end: formatDate(endOfToday(), 'yyyy-LL-dd'),
});

export const getStartEndOfDayUtc = (date, isEnd = false) => {
  const day = new Date(date);

  return isEnd ? endOfDay(day).toISOString() : startOfDay(day).toISOString();
};

export const addDaysFromSpecificHour = (daysCount = 7, startHours = 12) =>
  addDays(addHours(startOfDay(new Date()), startHours), daysCount);

export const addNumberOfDays = (date, daysCount = 7) =>
  addDays(new Date(date), daysCount);

export const substractNumberOfDays = (date, daysCount = 1) =>
  subDays(new Date(date), daysCount);

export const checkIsSameDay = (firstDate, secondDate) =>
  isSameDay(new Date(firstDate), new Date(secondDate));
export const checkIsSameYear = (laterDate, earlierDate) =>
  isSameYear(new Date(laterDate), new Date(earlierDate));

export const isSameDate = (firstDate, secondDate) =>
  isEqual(new Date(firstDate), new Date(secondDate));

export const addHoursToCurrentStartHour = (hours = 1) =>
  addHours(startOfHour(new Date()), hours);

export const getQuarterFromDate = date => getQuarter(new Date(date));

export const checkIsDateAfterPrevious = (date, previousDate) =>
  isAfter(new Date(date), new Date(previousDate));

export const getStartOfYear = date =>
  startOfYear(date ? new Date(date) : new Date());

export const getEndOfYear = date =>
  endOfYear(date ? new Date(date) : new Date());

export const getDifferenceInCalendarYears = (laterDate, earlierDate) => {
  return differenceInCalendarYears(new Date(laterDate), new Date(earlierDate));
};

export const checkIfDateIsInRange = (dateToCheck, startDate, endDate) =>
  isWithinInterval(new Date(dateToCheck), {
    start: new Date(startDate),
    end: new Date(endDate),
  });

export const getDayOfTheMonth = date => getDate(new Date(date));

export const getMonthFromDate = date => getMonth(new Date(date));

export const getYearFromDate = date => getYear(date);

export const addYearsToDate = (date = new Date(), years = 1) =>
  addYears(date, years);

export const subYearsFromDate = (date = new Date(), years = 1) =>
  subYears(date, years);

export const disableLeapDay = date => {
  const isFebruary = getMonth(date) === 1;
  const is29th = getDate(date) === 29;

  return isFebruary && is29th;
};
